/**
 * Assignment Panel Component
 * Allows instructors to assign quizzes to students
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUsers, FiCheckCircle, FiArrowLeft, FiClock, FiAlertCircle } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';

const AssignmentsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  background-color: var(--background-secondary);
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 0 0.75rem;
  }
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    border-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 1.875rem;
  font-weight: 700;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin: 0;
`;

const AssignmentCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  position: relative;
  transition: var(--transition);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-dark) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-lg);
    transform: translateY(-2px);
    
    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    border-radius: 0;
    margin-bottom: 1rem;
  }
`;

const AssignmentHeader = styled.div`
  padding: 1.75rem 1.5rem 1.5rem;
  background: var(--background-primary);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.25rem 1rem 1rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const AssignmentInfo = styled.div`
  flex: 1;
`;

const AssignmentTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.375rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  letter-spacing: -0.025em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0.375rem;
  }
`;

const AssignmentContent = styled.div`
  padding: 1.5rem;
  background: var(--background-primary);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;


const DateTimeInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  background: var(--background-primary);
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: var(--text-primary);
  position: relative;
  cursor: pointer;
  
  &::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3e%3c/svg%3e");
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 40px;
  }

  &:hover {
    border-color: var(--border-dark);
    background: var(--background-primary);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: var(--background-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: var(--text-muted);
    font-style: italic;
  }

  /* Firefox */
  &::-moz-calendar-picker-indicator {
    background: transparent;
    color: transparent;
    cursor: pointer;
    height: 20px;
    width: 20px;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3e%3c/svg%3e");
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: center;
    margin-right: 8px;
  }
`;

const CustomInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  background: var(--background-primary);
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: var(--text-primary);

  &:hover {
    border-color: var(--border-dark);
    background: var(--background-primary);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: var(--background-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: var(--text-muted);
    font-style: italic;
  }
`;

const TimePickerContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
`;

const TimePickerGroup = styled.div`
  flex: 1;
`;

const CustomOptionContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  margin-top: 0.5rem;
`;

const CustomOptionGroup = styled.div`
  flex: 1;
`;


const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  background: var(--background-primary);
  cursor: pointer;
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
`;

const StudentList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  background: var(--background-secondary);
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-primary);
  }
`;

const StudentInfo = styled.div`
  flex: 1;
`;

const StudentName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const StudentEmail = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(216, 184, 137, 0.1);

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 0;
  background-color: var(--background-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &.primary {
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(216, 184, 137, 0.3);
    
    &:hover {
      background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-color) 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(216, 184, 137, 0.4);
    }
  }
  
  &.secondary {
    background: linear-gradient(135deg, var(--text-muted) 0%, var(--contrast-accent) 100%);
    color: var(--text-inverse);
    border-color: var(--text-muted);
    box-shadow: var(--shadow-md);
    
    &:hover {
      background: linear-gradient(135deg, var(--contrast-accent) 0%, var(--text-muted) 100%);
      transform: translateY(-2px);
      box-shadow: var(--shadow-lg);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
    flex: 1;
    justify-content: center;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--danger-color);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
  }
`;

const SuccessState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--success-color);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
  }
`;

function AssignmentPanel() {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignmentData, setAssignmentData] = useState({
    course_id: '',
    due_at: '',
    attempts_allowed: 1,
    time_limit_minutes: null,
    immediate_feedback: true
  });
  const [customTimeLimit, setCustomTimeLimit] = useState('');
  const [customAttempts, setCustomAttempts] = useState('');
  const [useCustomTimeLimit, setUseCustomTimeLimit] = useState(false);
  const [useCustomAttempts, setUseCustomAttempts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch quiz and courses data
      const [quizResponse, coursesResponse] = await Promise.all([
        quizApiService.getQuiz(quizId),
        quizApiService.getCourses()
      ]);
      
      if (!quizResponse.success) {
        setError('Failed to fetch quiz');
        return;
      }
      
      if (!coursesResponse.success) {
        setError('Failed to fetch courses');
        return;
      }
      
      setQuiz(quizResponse.data);
      const allCourses = coursesResponse.courses || coursesResponse.data || [];
      // Filter to only show active courses that the instructor teaches
      const activeCourses = allCourses.filter(course => 
        course.is_active === 1 || course.is_active === true
      );
      setCourses(activeCourses);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchStudentsForCourse = useCallback(async (courseId) => {
    if (!courseId) {
      setStudents([]);
      return;
    }

    try {
      const studentsResponse = await quizApiService.getStudentsByCourse(courseId);
      
      if (studentsResponse.success) {
        setStudents(studentsResponse.data);
        setSelectedStudents([]); // Reset selected students when course changes
      } else {
        setError('Failed to fetch students for this course');
        setStudents([]);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students for this course');
      setStudents([]);
    }
  }, []);

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const handleAssignmentChange = (field, value) => {
    setAssignmentData(prev => ({ ...prev, [field]: value }));
    
    // If course is changed, fetch students for that course
    if (field === 'course_id') {
      fetchStudentsForCourse(value);
    }
  };

  const handleCustomTimeLimitChange = (value) => {
    setCustomTimeLimit(value);
    if (value && !isNaN(value) && value > 0) {
      setAssignmentData(prev => ({ ...prev, time_limit_minutes: parseInt(value) }));
    }
  };

  const handleCustomAttemptsChange = (value) => {
    setCustomAttempts(value);
    if (value && !isNaN(value) && value > 0) {
      setAssignmentData(prev => ({ ...prev, attempts_allowed: parseInt(value) }));
    }
  };

  const handleTimeLimitSelectChange = (value) => {
    if (value === 'custom') {
      setUseCustomTimeLimit(true);
    } else {
      setUseCustomTimeLimit(false);
      setCustomTimeLimit('');
      setAssignmentData(prev => ({ ...prev, time_limit_minutes: value ? parseInt(value) : null }));
    }
  };

  const handleAttemptsSelectChange = (value) => {
    if (value === 'custom') {
      setUseCustomAttempts(true);
    } else {
      setUseCustomAttempts(false);
      setCustomAttempts('');
      setAssignmentData(prev => ({ ...prev, attempts_allowed: parseInt(value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!assignmentData.course_id) {
      setError('Please select a course');
      return;
    }
    
    if (selectedStudents.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await quizApiService.assignQuiz(quizId, {
        course_id: assignmentData.course_id,
        studentIds: selectedStudents,
        due_at: assignmentData.due_at || null,
        attempts_allowed: assignmentData.attempts_allowed,
        time_limit_minutes: assignmentData.time_limit_minutes || null,
        immediate_feedback: assignmentData.immediate_feedback
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/instructor/quizzes');
        }, 2000);
      } else {
        setError('Failed to assign quiz');
      }
    } catch (err) {
      console.error('Error assigning quiz:', err);
      setError('Failed to assign quiz');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AssignmentsContainer>
        <Header>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
          <Title>Assign Quiz</Title>
        </Header>
        <LoadingState>
          <FiClock size={48} />
          <h3>Loading...</h3>
          <p>Please wait while we fetch the quiz and student data.</p>
        </LoadingState>
      </AssignmentsContainer>
    );
  }

  if (error && !success) {
    return (
      <AssignmentsContainer>
        <Header>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
          <Title>Assign Quiz</Title>
        </Header>
        <ErrorState>
          <FiAlertCircle size={48} />
          <h3>Error loading data</h3>
          <p>{error}</p>
        </ErrorState>
      </AssignmentsContainer>
    );
  }

  if (success) {
    return (
      <AssignmentsContainer>
        <Header>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
          <Title>Assign Quiz</Title>
        </Header>
        <SuccessState>
          <FiCheckCircle size={48} />
          <h3>Quiz Assigned Successfully!</h3>
          <p>Redirecting to quiz list...</p>
        </SuccessState>
      </AssignmentsContainer>
    );
  }

  return (
    <AssignmentsContainer>
      <Header>
        <BackButton onClick={() => navigate('/instructor/quizzes')}>
          <FiArrowLeft size={16} />
          Back to Quizzes
        </BackButton>
      </Header>

      <AssignmentCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AssignmentHeader>
          <AssignmentInfo>
            <AssignmentTitle>Quiz Assignment</AssignmentTitle>
            <div style={{ 
              color: 'var(--text-secondary)', 
              fontSize: '0.875rem', 
              marginTop: '0.5rem',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
            }}>
              Quiz: {quiz?.title}
            </div>
          </AssignmentInfo>
        </AssignmentHeader>
        
        <AssignmentContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="course_id">Select Course *</Label>
              <Select
                id="course_id"
                value={assignmentData.course_id}
                onChange={(e) => handleAssignmentChange('course_id', e.target.value)}
                required
              >
                <option value="">Choose a course...</option>
                {courses && courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.name} ({course.code})
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>
                Select Students {assignmentData.course_id && courses ? `from ${courses.find(c => c.id === assignmentData.course_id)?.name || 'selected course'}` : ''} *
              </Label>
              {assignmentData.course_id ? (
                <>
                  <CheckboxGroup>
                    <Checkbox
                      type="checkbox"
                      checked={selectedStudents.length === students.length && students.length > 0}
                      onChange={handleSelectAll}
                    />
                    <Label>Select All ({students.length} students)</Label>
                  </CheckboxGroup>
                  
                  <StudentList>
                    {students.map(student => (
                      <StudentItem key={student.id}>
                        <Checkbox
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleStudentToggle(student.id)}
                        />
                        <StudentInfo>
                          <StudentName>{student.name}</StudentName>
                          <StudentEmail>{student.email}</StudentEmail>
                        </StudentInfo>
                      </StudentItem>
                    ))}
                  </StudentList>
                </>
              ) : (
                <div style={{ 
                  padding: '1rem', 
                  textAlign: 'center', 
                  color: 'var(--text-muted)', 
                  fontStyle: 'italic',
                  backgroundColor: 'var(--background-secondary)',
                  borderRadius: 0,
                  border: '1px dashed var(--border-color)'
                }}>
                  Please select a course first to see available students
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="due_at">Due Date/Time (Optional)</Label>
              <TimePickerContainer>
                <TimePickerGroup>
                  <Label htmlFor="due_date" style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>Date</Label>
                  <DateTimeInput
                    id="due_date"
                    type="date"
                    value={assignmentData.due_at ? new Date(assignmentData.due_at).toISOString().slice(0, 10) : ''}
                    onChange={(e) => {
                      const dateValue = e.target.value;
                      const timeValue = assignmentData.due_at ? new Date(assignmentData.due_at).toTimeString().slice(0, 5) : '00:00';
                      if (dateValue) {
                        const newDateTime = new Date(`${dateValue}T${timeValue}`);
                        handleAssignmentChange('due_at', newDateTime.toISOString());
                      } else {
                        handleAssignmentChange('due_at', '');
                      }
                    }}
                  />
                </TimePickerGroup>
                <TimePickerGroup>
                  <Label htmlFor="due_time" style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>Time</Label>
                  <DateTimeInput
                    id="due_time"
                    type="time"
                    value={assignmentData.due_at ? new Date(assignmentData.due_at).toTimeString().slice(0, 5) : '00:00'}
                    onChange={(e) => {
                      const timeValue = e.target.value;
                      const dateValue = assignmentData.due_at ? new Date(assignmentData.due_at).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
                      if (timeValue) {
                        const newDateTime = new Date(`${dateValue}T${timeValue}`);
                        handleAssignmentChange('due_at', newDateTime.toISOString());
                      } else {
                        // If time is cleared, set to 12:00 AM (00:00)
                        const newDateTime = new Date(`${dateValue}T00:00`);
                        handleAssignmentChange('due_at', newDateTime.toISOString());
                      }
                    }}
                  />
                </TimePickerGroup>
              </TimePickerContainer>
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                marginTop: '0.5rem',
                fontStyle: 'italic',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
              }}>
                💡 Select both date and time for the assignment deadline
              </div>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="time_limit_minutes">Time Limit (Optional)</Label>
              <Select
                id="time_limit_minutes"
                value={useCustomTimeLimit ? 'custom' : (assignmentData.time_limit_minutes || '')}
                onChange={(e) => handleTimeLimitSelectChange(e.target.value)}
              >
                <option value="">No time limit</option>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
                <option value="180">3 hours</option>
                <option value="240">4 hours</option>
                <option value="300">5 hours</option>
                <option value="custom">Other (specify below)</option>
              </Select>
              
              {useCustomTimeLimit && (
                <CustomOptionContainer>
                  <CustomOptionGroup>
                    <CustomInput
                      type="number"
                      placeholder="Enter minutes (e.g., 75)"
                      value={customTimeLimit}
                      onChange={(e) => handleCustomTimeLimitChange(e.target.value)}
                      min="1"
                      max="1440"
                    />
                  </CustomOptionGroup>
                </CustomOptionContainer>
              )}
              
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                marginTop: '0.5rem',
                fontStyle: 'italic',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
              }}>
                ⏱️ Set a time limit for students to complete the quiz (max 24 hours)
              </div>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="attempts_allowed">Attempts Allowed</Label>
              <Select
                id="attempts_allowed"
                value={useCustomAttempts ? 'custom' : assignmentData.attempts_allowed}
                onChange={(e) => handleAttemptsSelectChange(e.target.value)}
              >
                <option value={1}>1 attempt</option>
                <option value={2}>2 attempts</option>
                <option value={3}>3 attempts</option>
                <option value={5}>5 attempts</option>
                <option value={10}>10 attempts</option>
                <option value={15}>15 attempts</option>
                <option value={20}>20 attempts</option>
                <option value={50}>50 attempts</option>
                <option value={100}>100 attempts</option>
                <option value="custom">Other (specify below)</option>
              </Select>
              
              {useCustomAttempts && (
                <CustomOptionContainer>
                  <CustomOptionGroup>
                    <CustomInput
                      type="number"
                      placeholder="Enter number of attempts (e.g., 7)"
                      value={customAttempts}
                      onChange={(e) => handleCustomAttemptsChange(e.target.value)}
                      min="1"
                      max="999"
                    />
                  </CustomOptionGroup>
                </CustomOptionContainer>
              )}
              
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                marginTop: '0.5rem',
                fontStyle: 'italic',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
              }}>
                🔄 Number of times students can take this quiz
              </div>
            </FormGroup>

            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  id="immediate_feedback"
                  type="checkbox"
                  checked={assignmentData.immediate_feedback}
                  onChange={(e) => handleAssignmentChange('immediate_feedback', e.target.checked)}
                />
                <Label htmlFor="immediate_feedback">Show immediate feedback after submission</Label>
              </CheckboxGroup>
              
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                marginTop: '0.5rem',
                fontStyle: 'italic',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
              }}>
                📊 Students will see their results and correct answers immediately after submitting
              </div>
            </FormGroup>

            {error && (
              <div style={{ 
                background: 'rgba(252, 129, 129, 0.1)', 
                color: 'var(--error-color)', 
                padding: '1rem', 
                borderRadius: 0, 
                marginBottom: '1rem',
                border: '1px solid var(--error-color)'
              }}>
                {error}
              </div>
            )}

            <ButtonGroup>
              <ActionButton 
                type="button" 
                className="secondary" 
                onClick={() => navigate('/instructor/quizzes')}
              >
                Cancel
              </ActionButton>
              <ActionButton 
                type="submit" 
                className="primary" 
                disabled={saving || selectedStudents.length === 0}
              >
                <FiUsers size={16} />
                {saving ? 'Assigning...' : `Assign to ${selectedStudents.length} Students`}
              </ActionButton>
            </ButtonGroup>
          </form>
        </AssignmentContent>
      </AssignmentCard>
    </AssignmentsContainer>
  );
}

export default AssignmentPanel;
